import { normalizeCanonicalSummary } from "../../behavior/utils/canonicalSummary";
import { resolveSprintWindow } from "../utils/trustbudDates";

const warningKeys = new Set();

function warnOnce(key, message, detail = {}) {
  if (process.env.NODE_ENV === "production" || warningKeys.has(key)) return;
  warningKeys.add(key);
  // eslint-disable-next-line no-console
  console.warn(`[TrustBud consistency] ${message}`, detail);
}

function getCanonicalBehaviorSummary({ behaviorSummary = null, summary = null, payload = null } = {}) {
  return behaviorSummary || payload?.behavior_summary || summary?.behavior_summary || null;
}

function getViewerSummary(input = {}) {
  return getCanonicalBehaviorSummary(input)?.viewer || null;
}

function getSprintSummary(input = {}) {
  return (
    getCanonicalBehaviorSummary(input)?.sprint ||
    input.relationship?.behavior_summary?.sprint ||
    null
  );
}

export function getViewerActiveDaysLast7({
  behaviorSummary = null,
  summary = null,
  localFallback = null,
  sourceLabel = "unknown",
} = {}) {
  const viewer = getViewerSummary({ behaviorSummary, summary });
  const canonical = viewer?.active_days_last_7;
  const normalized = summary ? normalizeCanonicalSummary(summary).activeDaysLast7 : null;
  if (canonical != null && localFallback != null && Number(canonical) !== Number(localFallback)) {
    warnOnce(`viewer-active-days-${sourceLabel}`, "Canonical viewer active days disagree with local fallback.", {
      sourceLabel,
      canonical,
      localFallback,
    });
  }
  return Number(canonical ?? normalized ?? localFallback ?? 0);
}

export function getViewerActedToday({
  behaviorSummary = null,
  summary = null,
  localFallback = null,
  sourceLabel = "unknown",
} = {}) {
  const viewer = getViewerSummary({ behaviorSummary, summary });
  const canonical = viewer?.acted_today;
  const normalized = summary ? normalizeCanonicalSummary(summary).actedToday : null;
  if (canonical != null && localFallback != null && Boolean(canonical) !== Boolean(localFallback)) {
    warnOnce(`viewer-acted-today-${sourceLabel}`, "Canonical acted-today disagrees with local fallback.", {
      sourceLabel,
      canonical,
      localFallback,
    });
  }
  return Boolean(canonical ?? normalized ?? localFallback ?? false);
}

export function getViewerUsageStreak({
  behaviorSummary = null,
  summary = null,
  localFallback = null,
} = {}) {
  const viewer = getViewerSummary({ behaviorSummary, summary });
  const explicitSummaryValue =
    summary?.usage_streak ??
    summary?.usageStreak ??
    summary?.current_usage_streak ??
    null;
  return Number(viewer?.usage_streak ?? explicitSummaryValue ?? localFallback ?? 0);
}

export function getViewerActionCounts({
  behaviorSummary = null,
  summary = null,
  localFallback = {},
  sourceLabel = "unknown",
} = {}) {
  const viewer = getViewerSummary({ behaviorSummary, summary });
  const normalized = summary ? normalizeCanonicalSummary(summary) : {};
  const canonicalCompletedToday = Number(
    viewer?.actions_completed_today ?? normalized.actionsCompletedToday ?? NaN
  );
  const canonicalCompletedThisWeek = Number(
    viewer?.actions_completed_this_week ?? normalized.actionsCompletedThisWeek ?? NaN
  );
  if (
    Number.isFinite(canonicalCompletedToday) &&
    localFallback.completedToday != null &&
    canonicalCompletedToday !== Number(localFallback.completedToday)
  ) {
    warnOnce(
      `viewer-actions-completed-today-${sourceLabel}`,
      "Canonical completed-today count disagrees with local fallback.",
      {
        sourceLabel,
        canonical: canonicalCompletedToday,
        localFallback: localFallback.completedToday,
      }
    );
  }
  if (
    Number.isFinite(canonicalCompletedThisWeek) &&
    localFallback.completedThisWeek != null &&
    canonicalCompletedThisWeek !== Number(localFallback.completedThisWeek)
  ) {
    warnOnce(
      `viewer-actions-completed-this-week-${sourceLabel}`,
      "Canonical completed-this-week count disagrees with local fallback.",
      {
        sourceLabel,
        canonical: canonicalCompletedThisWeek,
        localFallback: localFallback.completedThisWeek,
      }
    );
  }
  return {
    completedToday: Number(
      viewer?.actions_completed_today ??
        normalized.actionsCompletedToday ??
        localFallback.completedToday ??
        0
    ),
    openToday: Number(
      viewer?.actions_open_today ??
        normalized.actionsOpenToday ??
        localFallback.openToday ??
        0
    ),
    completedThisWeek: Number(
      viewer?.actions_completed_this_week ??
        normalized.actionsCompletedThisWeek ??
        localFallback.completedThisWeek ??
        0
    ),
  };
}

export function getSprintWindow({
  behaviorSummary = null,
  relationship = null,
  sprint = null,
  connection = null,
  sourceLabel = "unknown",
} = {}) {
  const sprintSummary = getSprintSummary({ behaviorSummary, relationship });
  const sprintEndDate = sprint?.end_date || null;
  const canonicalEndDate = sprintSummary?.end_date || null;
  const legacyEndDate = connection?.end_date || null;
  if (sprintEndDate && legacyEndDate && sprintEndDate !== legacyEndDate) {
    warnOnce(`sprint-end-date-${sourceLabel}`, "Active sprint end date differs from legacy connection end date.", {
      sourceLabel,
      sprintEndDate,
      legacyEndDate,
    });
  }
  const resolved = resolveSprintWindow({
    endDate: sprintEndDate || canonicalEndDate || legacyEndDate || null,
    localDate: getCanonicalBehaviorSummary({ behaviorSummary })?.local_date || sprintSummary?.local_date || null,
  });
  if (
    sprintSummary &&
    typeof sprintSummary.ends_today === "boolean" &&
    sprintSummary.ends_today !== resolved.endsToday
  ) {
    warnOnce(`sprint-window-${sourceLabel}`, "Sprint window helper disagrees with canonical sprint summary.", {
      sourceLabel,
      canonicalEndsToday: sprintSummary.ends_today,
      resolvedEndsToday: resolved.endsToday,
      endDate: sprintEndDate || canonicalEndDate || legacyEndDate || null,
    });
  }
  return {
    ...resolved,
    startDate: sprint?.start_date || sprintSummary?.start_date || connection?.start_date || null,
    active: Boolean(sprintSummary?.active ?? sprint?.id),
  };
}

export function getPartnerActedToday({
  relationship = null,
  localFallback = null,
  sourceLabel = "unknown",
} = {}) {
  const sprintSummary = getSprintSummary({ relationship });
  const canonical = sprintSummary?.partner_acted_today;
  if (canonical != null && localFallback != null && Boolean(canonical) !== Boolean(localFallback)) {
    warnOnce(`partner-acted-today-${sourceLabel}`, "Canonical partner activity disagrees with local fallback.", {
      sourceLabel,
      canonical,
      localFallback,
    });
  }
  return Boolean(canonical ?? localFallback ?? false);
}

export function getSharedActivityToday({
  relationship = null,
  localFallback = null,
} = {}) {
  const sprintSummary = getSprintSummary({ relationship });
  return Boolean(sprintSummary?.shared_activity_today ?? localFallback ?? false);
}

export function getPartnerLastActivityAt({ relationship = null, localFallback = null } = {}) {
  const sprintSummary = getSprintSummary({ relationship });
  return sprintSummary?.partner_last_activity_at || localFallback || null;
}
